import { useState } from 'preact/hooks';
import { MAX_TRADE_QTY } from '../../game/actions';
import type { GameController } from '../../core/GameController';
import {
  BUYABLE_FLOWERS,
  FLOWER_IDS,
  FLOWERS,
  RARITY_INFO,
  type FlowerId,
} from '../../game/flowers';
import { FEATURED_MULTIPLIER, marketMultiplier, sellPrice } from '../../game/market';
import { levelFromXp } from '../../game/progression';
import { UPGRADE_IDS, UPGRADES, maxLevel, upgradeCost } from '../../game/upgrades';
import { Panel, Tabs } from '../components/Panel';
import { useGameState, useServices } from '../context';

type MarketTab = 'sell' | 'seeds' | 'upgrades';

/** Sells a whole stack, split into chunks the rules engine accepts. */
function sellStack(game: GameController, species: FlowerId, golden: boolean, count: number): void {
  for (let left = count; left > 0; left -= MAX_TRADE_QTY) {
    game.dispatch({ type: 'sell', species, golden, qty: Math.min(left, MAX_TRADE_QTY) });
  }
}

const TABS = [
  { id: 'sell', label: '💰 Sell' },
  { id: 'seeds', label: '🌱 Seeds' },
  { id: 'upgrades', label: '🛠️ Upgrades' },
] as const;

function Trend({ species }: { species: FlowerId }) {
  const state = useGameState();
  const m = marketMultiplier(state, species);
  if (m >= FEATURED_MULTIPLIER) return <span class="trend featured">⭐ featured</span>;
  if (m >= 1.08) return <span class="trend up">▲ {Math.round((m - 1) * 100)}%</span>;
  if (m <= 0.92) return <span class="trend down">▼ {Math.round((1 - m) * 100)}%</span>;
  return <span class="trend flat">● steady</span>;
}

function RarityChip({ species }: { species: FlowerId }) {
  const rarity = RARITY_INFO[FLOWERS[species].rarity];
  return (
    <span class="rarity" style={{ background: rarity.color }}>
      {rarity.label}
    </span>
  );
}

function SellTab() {
  const state = useGameState();
  const { game } = useServices();
  const rows = FLOWER_IDS.flatMap((id) => {
    const stock = state.flowers[id];
    const out: Array<{ id: FlowerId; golden: boolean; count: number }> = [];
    if (stock?.n) out.push({ id, golden: false, count: stock.n });
    if (stock?.g) out.push({ id, golden: true, count: stock.g });
    return out;
  });
  const total = rows.reduce((sum, r) => sum + sellPrice(state, r.id, r.golden) * r.count, 0);

  if (rows.length === 0) {
    return (
      <p class="empty">No flowers in your basket yet — harvest some blooms from your field! 🌼</p>
    );
  }

  const sellAll = () => {
    for (const row of rows) sellStack(game, row.id, row.golden, row.count);
  };

  return (
    <>
      <div class="sell-all">
        <span>
          Basket worth <b>🪙{total.toLocaleString()}</b> today
        </span>
        <button class="big-button" onClick={sellAll}>
          Sell everything
        </button>
      </div>
      <ul class="rows">
        {rows.map(({ id, golden, count }) => {
          const def = FLOWERS[id];
          const price = sellPrice(state, id, golden);
          return (
            <li key={`${id}-${golden}`} class={`row ${golden ? 'golden' : ''}`}>
              <span class="row-icon">{def.emoji}</span>
              <div class="row-main">
                <b>
                  {golden ? '✨ Golden ' : ''}
                  {def.name}
                </b>{' '}
                <span class="muted">×{count}</span>
                <div class="row-sub">
                  🪙{price} each <Trend species={id} />
                </div>
              </div>
              <div class="row-actions">
                <button
                  class="small-button"
                  onClick={() => game.dispatch({ type: 'sell', species: id, golden, qty: 1 })}
                >
                  Sell 1
                </button>
                <button
                  class="small-button primary"
                  onClick={() => sellStack(game, id, golden, count)}
                >
                  All · 🪙{(price * count).toLocaleString()}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function SeedsTab() {
  const state = useGameState();
  const { game } = useServices();
  const level = levelFromXp(state.xp);
  return (
    <ul class="rows">
      {BUYABLE_FLOWERS.map((id) => {
        const def = FLOWERS[id];
        const cost = def.seedCost ?? 0;
        const locked = level < def.unlockLevel;
        return (
          <li key={id} class={`row ${locked ? 'locked' : ''}`}>
            <span class="row-icon">{locked ? '🔒' : def.emoji}</span>
            <div class="row-main">
              <b>{def.name}</b> <RarityChip species={id} />
              <div class="row-sub">
                {locked
                  ? `Unlocks at level ${def.unlockLevel}`
                  : `⏱ ${def.growTime}s · sells ~🪙${def.sellPrice} · you have ${state.seeds[id] ?? 0}`}
              </div>
            </div>
            {!locked && (
              <div class="row-actions">
                <button
                  class="small-button"
                  disabled={state.coins < cost}
                  onClick={() => game.dispatch({ type: 'buySeeds', species: id, qty: 1 })}
                >
                  1 · 🪙{cost}
                </button>
                <button
                  class="small-button primary"
                  disabled={state.coins < cost * 5}
                  onClick={() => game.dispatch({ type: 'buySeeds', species: id, qty: 5 })}
                >
                  5 · 🪙{cost * 5}
                </button>
              </div>
            )}
          </li>
        );
      })}
      <li class="row hint">
        <span class="row-icon">🐝</span>
        <div class="row-main">
          <b>Rare flowers can't be bought.</b>
          <div class="row-sub">
            Plant different flowers side by side — when you harvest one in bloom next to another,
            they might cross-pollinate!
          </div>
        </div>
      </li>
    </ul>
  );
}

function UpgradesTab() {
  const state = useGameState();
  const { game } = useServices();
  const level = levelFromXp(state.xp);
  return (
    <ul class="cards">
      {UPGRADE_IDS.map((id) => {
        const def = UPGRADES[id];
        const current = state.upgrades[id];
        const max = maxLevel(id);
        const cost = upgradeCost(id, current);
        const locked = level < def.unlockLevel;
        return (
          <li key={id} class={`card ${locked ? 'locked' : ''}`}>
            <div class="card-icon">{def.emoji}</div>
            <b>{def.name}</b>
            <div class="pips" aria-label={`Level ${current} of ${max}`}>
              {Array.from({ length: max }, (_, i) => (
                <span key={i} class={`pip ${i < current ? 'on' : ''}`} />
              ))}
            </div>
            <p class="card-text">
              {current < max ? def.describe(current + 1) : def.describe(current)}
            </p>
            {locked ? (
              <span class="muted">🔒 Level {def.unlockLevel}</span>
            ) : cost === null ? (
              <span class="maxed">MAX ✨</span>
            ) : (
              <button
                class="small-button primary"
                disabled={state.coins < cost}
                onClick={() => game.dispatch({ type: 'buyUpgrade', upgrade: id })}
              >
                {current === 0 ? 'Build' : 'Upgrade'} · 🪙{cost.toLocaleString()}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function MarketPanel() {
  const [tab, setTab] = useState<MarketTab>('sell');
  const state = useGameState();
  const featured = FLOWERS[state.market.featured];
  return (
    <Panel title="Market Stand" icon="🏪" wide>
      <div class="banner">
        ⭐ Today's favourite:{' '}
        <b>
          {featured.emoji} {featured.name}
        </b>{' '}
        sells for {Math.round((FEATURED_MULTIPLIER - 1) * 100)}% extra!
      </div>
      <Tabs<MarketTab> tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'sell' && <SellTab />}
      {tab === 'seeds' && <SeedsTab />}
      {tab === 'upgrades' && <UpgradesTab />}
    </Panel>
  );
}
